import { useRef, useEffect } from "react";

import { AC_STEP_USER_INFO, AC_STEP_EMAIL_VERIFICATION_SENT } from "../../utils/AccountCreationConstants";

import useAccountCreationData from "../../../../../../state/landingPages/accountCreation/UseAccountCreationData";
import useAccountCreationUserInfo from "../userInfo/UseAccountCreationUserInfo";
import { useAccountCreationFlow } from "../userInfo/useAccountCreationFlow";
import { useAccountCreationAPI } from "../userInfo/useAccountCreationAPI";

const useAccountCreationYouthContact = () => {
  const { accountCreationState, updateAccountCreationStep, updateAccountCreationPageData, postAccountCreationStep1, postInviteUser } = useAccountCreationData();
  const { prepareCreateStep1Data, isLoading: userInfoLoading } = useAccountCreationUserInfo();

  const {
    apiErrorPopupState,
    clearAPIError,
    handleAPICall,
    callInviteUser,
    isLoading: isAPILoading
  } = useAccountCreationAPI();

  // Use the same flow logic as UserInfo hook
  const {
    handleCreateStep1Response,
    handleInviteUserAndProceed
  } = useAccountCreationFlow({
    accountCreationState,
    updateAccountCreationStep,
    callInviteUser,
    postInviteUser
  });
  
  // Track if component is mounted to prevent state updates on unmounted component
  const isMountedRef = useRef(true);
  
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const onBackClicked = () => {
    updateAccountCreationStep(AC_STEP_USER_INFO);
  };

  const onCloseAPIErrorPopupClicked = () => {
    if (!isMountedRef.current) return;
    clearAPIError();
  };

  const onSubmitFormCallback = async (formState) => {
    // Save youth contact data
    updateAccountCreationPageData(formState, 'youthContactData');

    console.log("Youth contact form submitted: " + JSON.stringify(formState, null, 2));
    console.log("accountCreationState: " + JSON.stringify(accountCreationState, null, 2));
    
    // Get the current user info data
    const userInfoData = accountCreationState?.userInfoData || {};
    console.log("userInfoData: " + JSON.stringify(userInfoData, null, 2));
    
    // Merge user info with youth contact data
    const mergedUserInfoData = {
      ...userInfoData,
      ...formState
    };

    try {
      // Now call executeCreateStep1 with all collected data including youth contact info
      const createStep1Data = prepareCreateStep1Data(mergedUserInfoData, { 
        ...accountCreationState, 
        youthContactData: formState 
      });
      
      console.log("Calling executeCreateStep1 with data: " + JSON.stringify(createStep1Data, null, 2));
      
      // Call executeCreateStep1 with custom success handler for youth contact scenario
      await executeCreateStep1WithYouthContactHandler(createStep1Data, mergedUserInfoData);
      
    } catch (error) {
      console.error('Error during account creation step 1:', error);
      // Error handling is managed by handleAPICall in executeCreateStep1WithYouthContactHandler
      // Just rethrow to let the API handler manage it
      throw error;
    }
  };

  // Custom handler for executeCreateStep1 from youth contact that navigates to email verification sent
  const executeCreateStep1WithYouthContactHandler = async (createStep1Data, userInfoData) => {
    const postAccountCreationStep1Promise = postAccountCreationStep1(createStep1Data, false); // false = not ignoring matches

    await handleAPICall(
      postAccountCreationStep1Promise,
      (response) => handleYouthContactCreateStep1Success(response, userInfoData),
      'An error occurred during account creation.'
    );
  };

  // const handleAPICall = async (apiPromise, onSuccess, errorMessage = 'API call failed.') => {
  //   if (!apiPromise) {
  //     setAPIError('Unable to process request.');
  //     return;
  //   }

  //   if (isMountedRef.current) {
  //     setIsCreatingAccount(true);
  //   }
    
  //   try {
  //     const result = await apiPromise;
  //     if (onSuccess && isMountedRef.current) {
  //       onSuccess(result);
  //     }
  //     return result;
  //   } catch (error) {
  //     console.error('API call error:', error);
  //     if (isMountedRef.current) {
  //       setAPIError(error?.message || errorMessage);
  //     }
  //     throw error;
  //   } finally {
  //     if (isMountedRef.current) {
  //       setIsCreatingAccount(false);
  //     }
  //   }
  // };

  const handleYouthContactCreateStep1Success = (response, userInfoData) => {
    console.log('Processing CreateStep1 response from youth contact:', response);

    // Custom callback for youth contact - we've already collected youth info, so go to email verification
    const proceedToEmailVerification = () => {
      updateAccountCreationStep(AC_STEP_EMAIL_VERIFICATION_SENT, userInfoData, 'userInfoData');
    };

    // Use the same response handling logic as UserInfo hook
    //const result = handleCreateStep1Response(response, userInfoData, proceedToEmailVerification, false);

    if (!response.objData.success) {
      // Let the handleAPICall error handling manage this by throwing an error
      throw new Error(response.objData.errorMessage || 'Account creation failed. Please try again.');
    }
    else{
      handleInviteUserAndProceed(userInfoData);
      return;
    }

    // proceedDirectly case is handled by handleCreateStep1Response calling the callback (proceedToEmailVerification)
  };

  return {
    youthContactData: accountCreationState?.youthContactData || {},
    isLoading: userInfoLoading || isAPILoading,
    apiErrorPopupState,
    onBackClicked,
    onCloseAPIErrorPopupClicked,
    onSubmitFormCallback
  };
};

export default useAccountCreationYouthContact;